import React, { FC, useEffect, useState } from 'react';
import { Button, ToggleSwitch, Select } from 'flowbite-react';
import { isNumber, pick } from 'lodash';

import { InputLabel } from '../../../../components';
import { sites } from '../../../../components/GetSite';
import { IDevice, DeviceType } from '../../../../types';
import { updateDevice, resetDevice, stopDevice } from '../../../../services';
import { Msg } from '../../../../consts';
import { useGlobal } from '../../../../hooks/useGlobal';

// WhatsApp device types that support restart and stop operations
const WHATSAPP_DEVICE_TYPES: DeviceType[] = [
  DeviceType.INVITE,
  DeviceType.BOT,
  DeviceType.AGENT_WP,
];

interface DeviceInformationFormProps {
  deviceDetail?: IDevice;
  pageAction?: any;
  onUpdate?: () => void; // Optional callback for refresh
}

export const DeviceInformationForm: FC<DeviceInformationFormProps> = ({
  deviceDetail,
  pageAction,
  onUpdate,
}) => {
  const { showAlert } = useGlobal();
  const [formPayload, setFormPayload] = useState<{ [key: string]: any }>({});
  const [formLoading, setFormLoading] = useState<boolean>(false);
  const [resetLoading, setResetLoading] = useState<boolean>(false);
  const [stopLoading, setStopLoading] = useState<boolean>(false);

  // Initialize form data when deviceDetail changes
  useEffect(() => {
    if (deviceDetail) {
      const baseFields = [
        'name',
        'number',
        'port',
        'note',
        'proxyConfig',
        'priority',
        'isMessageSent',
        'customerId',
      ];

      // Add fields based on device type
      const deviceSpecificFields =
        deviceDetail.deviceType === DeviceType.BOT
          ? ['siteId', 'profilePicture', 'aboutText', 'displayName']
          : ['dailyLimit', 'dailyTotalMessage', 'siteId'];

      const allFields = [...baseFields, ...deviceSpecificFields];
      const payload = pick(deviceDetail, allFields);

      // Add profile fields for AGENT_WP
      if (
        deviceDetail.deviceType === DeviceType.AGENT_WP &&
        deviceDetail.profile
      ) {
        payload.profile = deviceDetail.profile;
      }

      setFormPayload(payload);
    }
  }, [deviceDetail]);

  const handleFormPayload = (e: any) => {
    const { name, value } = e.target;

    if (name === 'number' && !isNumber(value)) {
      return;
    }

    if (
      name === 'proxyUrl' ||
      name === 'proxyAuthUsername' ||
      name === 'proxyAuthPassword' ||
      name === 'proxyCountry'
    ) {
      const proxyConfig = formPayload.proxyConfig || {};
      if (name === 'proxyUrl') {
        proxyConfig.url = value;
      } else if (name === 'proxyAuthUsername') {
        proxyConfig.auth = {
          ...proxyConfig.auth,
          username: value,
        };
      } else if (name === 'proxyAuthPassword') {
        proxyConfig.auth = {
          ...proxyConfig.auth,
          password: value,
        };
      } else if (name === 'proxyCountry') {
        proxyConfig.country = value;
      }

      setFormPayload({
        ...formPayload,
        proxyConfig,
      });

      return;
    }

    // Handle profile fields for AGENT_WP
    if (name === 'profile.displayName' || name === 'profile.aboutText') {
      const profile = formPayload.profile || {};
      const fieldName = name.split('.')[1];
      setFormPayload({
        ...formPayload,
        profile: {
          ...profile,
          [fieldName]: value,
        },
      });
      return;
    }

    setFormPayload({
      ...formPayload,
      [name]: value,
    });
  };

  const deviceUpdateHandler = async (formData: any) => {
    setFormLoading(true);

    try {
      // Prepare update payload
      const updatePayload = { ...formData };

      // For AGENT_WP, include profile.displayName and profile.aboutText
      if (
        deviceDetail?.deviceType === DeviceType.AGENT_WP &&
        formData.profile
      ) {
        updatePayload.profile = {
          displayName: formData.profile.displayName || undefined,
          aboutText: formData.profile.aboutText || undefined,
          picture: formData.profile.picture || undefined,
        };
      }

      await updateDevice(deviceDetail?._id || '', updatePayload);

      showAlert({
        text: Msg.Success,
        icon: 'success',
      });

      // Call onUpdate callback if provided
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Device update failed:', error);
      showAlert({
        text: Msg.Error,
        icon: 'error',
      });
    } finally {
      setFormLoading(false);
    }
  };

  const deviceResetHandler = () => {
    showAlert({
      text: Msg.DeviceReset,
      icon: 'question',
      showCancelButton: true,
    }).then(async (result: any) => {
      if (result.isConfirmed) {
        setResetLoading(true);
        await resetDevice(deviceDetail?._id || '').then(() => {
          location.reload();
        });
      }
    });
  };

  const deviceStopHandler = () => {
    showAlert({
      text: Msg.DeviceStop,
      icon: 'question',
      showCancelButton: true,
    }).then(async (result: any) => {
      if (result.isConfirmed) {
        setStopLoading(true);
        await stopDevice(deviceDetail?._id || '').then(() => {
          location.reload();
        });
      }
    });
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    deviceUpdateHandler(formPayload);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="flex gap-4 flex-col">
        <div className="grid grid-cols-3 gap-6">
          <InputLabel
            title="Ad"
            value={formPayload?.name || ''}
            name="name"
            onChange={handleFormPayload}
            required
          />
          <InputLabel
            title="Numara"
            value={formPayload?.number || ''}
            name="number"
            type="number"
            onChange={handleFormPayload}
            readOnly
          />
          {deviceDetail?.deviceType === DeviceType.INVITE && (
            <InputLabel
              title="Günlük Mesaj Adet"
              value={formPayload?.dailyLimit || 0}
              name="dailyLimit"
              required
              type="number"
              onChange={handleFormPayload}
            />
          )}
          {deviceDetail?.deviceType === DeviceType.BOT && (
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-900">
                Site
              </label>
              <Select
                name="siteId"
                value={formPayload?.siteId || ''}
                onChange={handleFormPayload}
                required
              >
                <option value="">Site Seçiniz</option>
                {sites.map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.name}
                  </option>
                ))}
              </Select>
            </div>
          )}

          {(deviceDetail?.deviceType === DeviceType.AGENT_WP ||
            deviceDetail?.deviceType === DeviceType.AGENT_TG) && (
            <InputLabel
              title="Temsilci"
              value={formPayload?.customerId || ''}
              name="customerId"
              required
              onChange={handleFormPayload}
            />
          )}
        </div>
        {deviceDetail?.deviceType !== DeviceType.AGENT_TG && (
          <div className="grid grid-cols-3 gap-6">
            <InputLabel
              title="Proxy Url"
              value={formPayload?.proxyConfig?.url || ''}
              name="proxyUrl"
              required
              onChange={handleFormPayload}
            />
            <InputLabel
              title="Proxy Username"
              value={formPayload?.proxyConfig?.auth?.username || ''}
              name="proxyAuthUsername"
              required
              onChange={handleFormPayload}
            />
            <InputLabel
              title="Proxy Password"
              value={formPayload?.proxyConfig?.auth?.password || ''}
              name="proxyAuthPassword"
              onChange={handleFormPayload}
            />
          </div>
        )}
        <InputLabel
          title="Not"
          value={formPayload?.note || ''}
          multiline
          name="note"
          onChange={handleFormPayload}
        />

        {/* AGENT_WP and AGENT_TG Profile Fields */}
        {(deviceDetail?.deviceType === DeviceType.AGENT_WP ||
          deviceDetail?.deviceType === DeviceType.AGENT_TG) && (
          <div className="flex flex-col gap-4">
            <div className="border-t pt-4">
              <h3 className="text-lg font-semibold mb-4 text-gray-700">
                Profil Bilgileri
              </h3>
              <div className="grid grid-cols-2 gap-6">
                <InputLabel
                  title="Profil Adı"
                  value={formPayload?.profile?.displayName || ''}
                  name="profile.displayName"
                  onChange={handleFormPayload}
                />
                <InputLabel
                  title="Hakkında"
                  value={formPayload?.profile?.aboutText || ''}
                  name="profile.aboutText"
                  onChange={handleFormPayload}
                />
              </div>
            </div>
          </div>
        )}

        <div>
          <ToggleSwitch
            checked={formPayload?.isMessageSent || false}
            label="Mesaj Gönderim"
            onChange={(value: boolean) => {
              setFormPayload({
                ...formPayload,
                ['isMessageSent']: value,
              });
            }}
            name="isMessageSent"
          />
        </div>

        <div className="flex justify-between">
          <Button
            isProcessing={formLoading}
            disabled={formLoading || !pageAction?.edit}
            type="submit"
            color="blue"
          >
            Kaydet
          </Button>

          {pageAction?.actionButtons &&
            WHATSAPP_DEVICE_TYPES.includes(
              deviceDetail?.deviceType as DeviceType,
            ) && (
              <Button.Group>
                <Button
                  color="gray"
                  onClick={deviceResetHandler}
                  isProcessing={resetLoading}
                  disabled={resetLoading}
                >
                  Restart
                </Button>
                <Button
                  color="gray"
                  onClick={deviceStopHandler}
                  isProcessing={stopLoading}
                  disabled={stopLoading}
                >
                  Stop
                </Button>
              </Button.Group>
            )}
        </div>
      </div>
    </form>
  );
};
